import { TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import Table from '@mui/material/Table';
import { EmptyState } from '@qfa/react-components';
import { useVirtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';
import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTableStyles } from '../styles';
import VirtualRow from '../VirtualRow';
import VirtualTable, { TablePropsContext } from '../VirtualTable';
import { TableRowsProps } from './types';

const TableRows: React.FunctionComponent<TableRowsProps> = (props) => {
  const {
    onClick,
    data,
    onContextClick,
    orderedColumns,
    subRow: SubRowComponent,
    isSelected,
    setClassName,
    type,
  } = props;
  const { t } = useTranslation('common');
  const classes = useTableStyles();
  const parentRef = useRef<HTMLTableElement>(null);
  const virtualizer = useVirtualizer({
    count: data?.length ?? 0,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 45,
    overscan: 10,
  });

  const { hideColumnHeader = false } = useContext(TablePropsContext);

  const [expanded, setExpanded] = useState(new Map<string, true>());

  const isExpanded = useCallback((rowId: string) => !!expanded.get(rowId), [expanded]);

  const toggleExpand = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const rowId = e.currentTarget.dataset.id!;
    setExpanded((prev) => {
      const newMap = new Map(prev);
      prev.get(rowId) ? newMap.delete(rowId) : newMap.set(rowId, true);
      return newMap;
    });
  }, []);

  const getRow = useCallback(
    (index: number) => {
      return data?.[index] ?? ({} as any);
    },
    [data]
  );

  const items = virtualizer.getVirtualItems();
  const rowData = useMemo(() => {
    return {
      classes,
      expandOnClick: !onClick && !!SubRowComponent,
      getRow,
      isExpanded,
      isSelected,
      onClick,
      onContextClick,
      orderedColumns,
      setClassName,
      SubRowComponent,
      toggleExpand,
    };
  }, [
    SubRowComponent,
    classes,
    getRow,
    isExpanded,
    isSelected,
    onClick,
    onContextClick,
    orderedColumns,
    setClassName,
    toggleExpand,
  ]);

  if (!data?.length) {
    return (
      <Table style={{ height: '100%' }}>
        {!hideColumnHeader && (
          <TableHead>
            <TableRow>
              {orderedColumns?.map((column) => {
                const { headClassName, className, minWidth, title, name } = column;
                return (
                  <TableCell
                    key={name}
                    className={clsx(headClassName, className, classes.header, {
                      [classes.minWidthCell]: minWidth,
                    })}
                    style={{ minWidth: minWidth && typeof minWidth === 'number' ? minWidth : '' }}
                  >
                    {title}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
        )}
        <TableBody>
          <TableRow key='emptyState' className={classes.emptyStateTableRow}>
            <TableCell colSpan={1000}>
              <EmptyState dataType={type || t('items')} />
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    );
  }

  return (
    <VirtualTable
      ref={parentRef}
      style={{
        height: virtualizer.getTotalSize(),
        width: '100%',
        position: 'relative',
        minHeight: 'inherit',
        willChange: 'transform',
      }}
    >
      {items.map((virtualRow) => (
        <VirtualRow
          key={virtualRow.key as string}
          ref={virtualizer.measureElement}
          index={virtualRow.index}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            transform: `translateY(${items[0]?.start ?? 0}px)`,
          }}
          data={rowData}
        />
      ))}
    </VirtualTable>
  );
};

export default React.memo(TableRows);
